import React from 'react';
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  CardMedia,
  IconButton,
  useTheme,
  useMediaQuery,
  Divider,
} from '@mui/material';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import EngineeringIcon from '@mui/icons-material/Engineering';
import { motion } from 'framer-motion';

const equipmentSystems = [
  {
    id: 'reverse-osmosis',
    title: 'Reverse Osmosis Systems',
    description: 'Advanced RO systems for superior water purification with high recovery rates and energy efficiency.',
    image: 'https://source.unsplash.com/800x600/?water,filtration',
    features: [
      'High recovery rates',
      'Energy efficient design',
      'Advanced membrane technology',
      'Automated operation',
      'Remote monitoring capability'
    ]
  },
  {
    id: 'nano-filtration',
    title: 'Nano Filtration Systems',
    description: 'Specialized filtration systems for selective ion removal and organic compound separation.',
    image: 'https://source.unsplash.com/800x600/?industrial,filter',
    features: [
      'Selective ion removal',
      'Low operating pressure',
      'High flux rates',
      'Minimal chemical usage',
      'Compact design'
    ]
  },
  {
    id: 'belt-press',
    title: 'Belt Press Systems',
    description: 'Efficient dewatering solutions for sludge and slurry processing.',
    image: 'https://source.unsplash.com/800x600/?industrial,press',
    features: [
      'High solid capture rate',
      'Continuous operation',
      'Low energy consumption',
      'Automated controls',
      'Variable speed drive'
    ]
  },
  {
    id: 'cooling-tower',
    title: 'Cooling Tower Systems',
    description: 'Industrial cooling solutions with optimal thermal performance and water conservation.',
    image: 'https://source.unsplash.com/800x600/?industrial,tower',
    features: [
      'High thermal efficiency',
      'Water conservation features',
      'Corrosion resistant materials',
      'Low maintenance design',
      'Integrated distribution system'
    ]
  },
  {
    id: 'screener',
    title: 'Screening Systems',
    description: 'Advanced screening solutions for solid-liquid separation and particle removal.',
    image: 'https://source.unsplash.com/800x600/?industrial,screen',
    features: [
      'Multiple screen options',
      'Self-cleaning mechanism',
      'Robust construction',
      'High flow capacity',
      'Low headloss design'
    ]
  },
  {
    id: 'ultra-filtration',
    title: 'Ultra Filtration Systems',
    description: 'High-performance ultra filtration for superior water quality and contaminant removal.',
    image: 'https://source.unsplash.com/800x600/?industrial,filtration',
    features: [
      'High rejection rates',
      'Low fouling membranes',
      'Compact footprint',
      'Automated backwash',
      'Multiple membrane options'
    ]
  },
  {
    id: 'mbr',
    title: 'Membrane Bio-Reactor (MBR)',
    description: 'Advanced biological treatment with integrated membrane filtration.',
    image: 'https://source.unsplash.com/800x600/?industrial,biological',
    features: [
      'High quality effluent',
      'Small footprint',
      'Automated operation',
      'Low sludge production',
      'Energy efficient design'
    ]
  },
  {
    id: 'water-softener',
    title: 'Water Softening Systems',
    description: 'Efficient water softening solutions for scale prevention and improved water quality.',
    image: 'https://source.unsplash.com/800x600/?industrial,softener',
    features: [
      'Automatic regeneration',
      'High exchange capacity',
      'Low salt consumption',
      'Smart monitoring system',
      'Duplex configuration available'
    ]
  },
  {
    id: 'demineralization',
    title: 'Demineralization Systems',
    description: 'Complete demineralization solutions for ultra-pure water production.',
    image: 'https://source.unsplash.com/800x600/?industrial,demineralization',
    features: [
      'Mixed bed technology',
      'Automatic regeneration',
      'High purity output',
      'Online monitoring',
      'Custom capacity options'
    ]
  },
  {
    id: 'oxygen-generator',
    title: 'Oxygen Generation Systems',
    description: 'On-site oxygen generation systems for various industrial applications.',
    image: 'https://source.unsplash.com/800x600/?industrial,oxygen',
    features: [
      'PSA technology',
      'High purity output',
      'Energy efficient',
      'Automatic operation',
      'Continuous monitoring'
    ]
  },
  {
    id: 'cartridge-filters',
    title: 'Cartridge Filtration Systems',
    description: 'Versatile cartridge filtration solutions for various applications and particle sizes.',
    image: 'https://source.unsplash.com/800x600/?industrial,filter',
    features: [
      'Multiple micron ratings',
      'Easy cartridge replacement',
      'High flow capacity',
      'Various material options',
      'Custom housing designs'
    ]
  }
];

const EquipmentSystems = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [hoveredCard, setHoveredCard] = React.useState(null);

  return (
    <Box sx={{ bgcolor: '#f8f9fa' }}>
      {/* Hero Section */}
      <Box
        sx={{
          position: 'relative',
          height: { xs: '300px', md: '400px' },
          display: 'flex',
          alignItems: 'center',
          overflow: 'hidden',
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'linear-gradient(to right, rgba(0, 0, 0, 0.85), rgba(0, 0, 0, 0.45))',
            zIndex: 1
          }
        }}
      >
        <Box
          component="img"
          src="/images/products/equipment-hero.jpg"
          alt="Equipment Systems"
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            objectFit: 'cover'
          }}
        />
        <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 2 }}>
          <Box sx={{ maxWidth: '800px' }}>
            <Typography
              variant="h1"
              sx={{
                color: 'white',
                fontWeight: 600,
                fontSize: { xs: '2rem', md: '3rem' },
                lineHeight: 1.2,
                mb: 1,
                position: 'relative',
                '&::after': {
                  content: '""',
                  position: 'absolute',
                  bottom: '-12px',
                  left: '0',
                  width: '60px',
                  height: '4px',
                  backgroundColor: '#0288d1',
                  borderRadius: '2px'
                }
              }}
            >
              Equipment & Systems
            </Typography>
            <Typography
              variant="h5"
              sx={{
                color: '#e0e0e0',
                maxWidth: '600px',
                fontSize: { xs: '1rem', md: '1.25rem' },
                lineHeight: 1.5,
                fontWeight: 400,
                mt: 3
              }}
            >
              Comprehensive range of water treatment equipment and systems engineered for optimal performance and reliability
            </Typography>
          </Box>
        </Container>
      </Box>

      {/* Equipment Grid */}
      <Container maxWidth="lg" sx={{ py: { xs: 6, md: 10 } }}>
        <Grid container spacing={4}>
          {equipmentSystems.map((system, index) => (
            <Grid item xs={12} md={6} key={system.id}>
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
              >
                <Card
                  onMouseEnter={() => setHoveredCard(system.id)}
                  onMouseLeave={() => setHoveredCard(null)}
                  sx={{
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    borderRadius: 3,
                    overflow: 'hidden',
                    boxShadow: hoveredCard === system.id 
                      ? '0 16px 40px rgba(0,0,0,0.12)' 
                      : '0 4px 12px rgba(0,0,0,0.05)',
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      transform: 'translateY(-8px)'
                    }
                  }}
                >
                  <CardMedia
                    component="img"
                    height="240"
                    image={system.image}
                    alt={system.title}
                    sx={{
                      position: 'relative',
                      '&::after': {
                        content: '""',
                        position: 'absolute',
                        bottom: 0,
                        left: 0,
                        right: 0,
                        height: '30%',
                        background: 'linear-gradient(transparent, rgba(0,0,0,0.7))'
                      }
                    }}
                  />
                  <CardContent sx={{ flexGrow: 1, p: 4 }}>
                    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2, mb: 2 }}>
                      <EngineeringIcon sx={{ color: '#0288d1', fontSize: 28, mt: 0.5 }} />
                      <Box>
                        <Typography variant="h5" gutterBottom sx={{ fontWeight: 700, color: '#0288d1' }}>
                          {system.title}
                        </Typography>
                        <Typography variant="body1" sx={{ color: 'text.secondary', mb: 2 }}>
                          {system.description}
                        </Typography>
                      </Box>
                    </Box>

                    <Divider sx={{ mb: 3 }} />

                    <Box sx={{ mb: 2 }}>
                      {system.features.map((feature, idx) => (
                        <Typography
                          key={idx}
                          variant="body2"
                          sx={{
                            color: 'text.secondary',
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1,
                            mb: 1,
                            '&::before': {
                              content: '""',
                              width: '6px',
                              height: '6px',
                              bgcolor: '#0288d1',
                              borderRadius: '50%'
                            }
                          }}
                        >
                          {feature}
                        </Typography>
                      ))}
                    </Box>

                    <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                      <IconButton
                        sx={{
                          color: '#0288d1',
                          '&:hover': {
                            bgcolor: 'rgba(2,136,209,0.1)'
                          }
                        }}
                      >
                        <ArrowForwardIcon />
                      </IconButton>
                    </Box>
                  </CardContent>
                </Card>
              </motion.div>
            </Grid>
          ))}
        </Grid>
      </Container>
    </Box>
  );
};

export default EquipmentSystems; 